import { useAppSearchParams } from '@/hooks/useAppSearchParams.ts';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  OptionType,
  PickemLeaderboardData,
  SelectionType,
  TournamentLeaderboardData,
} from '@/@types/api.types.ts';
import { useGetPickemLeaderboard } from '@/api/pickem-leaderboard/getPickemLeaderboard.ts';
import { useGetPickem } from '@/api/pickems/getPickem.ts';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Card,
  CircularProgress,
  Container,
  MenuItem,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { PickemControlBar } from '@/pages/LeaderboardPage/PickemControlBar/index.tsx';
import { PickemLeaderboardTable } from '@/components/pickem-edit/PickemLeaderboardTable/index.tsx';
import Iconify from '@/components/iconify/index.tsx';

export const PickemLeaderboard = () => {
  const { pickemId } = useAppSearchParams();

  const navigate = useNavigate();
  const [leaderboardData, setLeaderboardData] = useState<
    PickemLeaderboardData | TournamentLeaderboardData | null
  >(null);
  const [open, setOpen] = useState(null);
  const [openId, setOpenId] = useState(null);

  const { data: pickemLeaderboardData, isLoading: pickemIsLoading } =
    useGetPickemLeaderboard({
      pickemId,
      offset: 0,
      limit: 50,
    });

  const { data: pickem } = useGetPickem(pickemId);

  const dataSource = pickemLeaderboardData;

  useEffect(() => {
    setLeaderboardData(dataSource ?? null);
  }, [dataSource]);

  useEffect(() => {
    if (!pickem || !dataSource) return;

    const answers_leaderboard = dataSource.leaderboard.map(
      (userLeaderboard) => {
        const answers = userLeaderboard.selections.reduce(
          (accum, selection: SelectionType) => {
            const question = pickem.questions.find(
              (item) => item.question_id === selection.question_id
            );
            if (question) {
              if (question.is_tiebreak) {
                accum.custom_value =
                  +question.tiebreak_score === +selection.custom_value;
              }
              const option = question.options.find(
                (item: OptionType) => item.option_id === selection.option_id
              );
              if (option) {
                if (!!option.is_correct) {
                  accum.correct++;
                }
                if (!option.is_correct) {
                  accum.incorrect++;
                }
              }
            }
            return accum;
          },
          { correct: 0, incorrect: 0, custom_value: false }
        );
        return { ...userLeaderboard, answers };
      }
    );

    setLeaderboardData({
      ...dataSource,
      leaderboard: answers_leaderboard,
    } as any);
  }, [pickem, dataSource]);

  const handleCloseMenu = () => {
    setOpen(null);
    setOpenId(null);
  };

  return (
    <>
      <Helmet>
        <title>Leaderboard </title>
      </Helmet>

      <Container>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          mb={5}
        >
          <Typography variant='h4' gutterBottom>
            Pickem Leaderboard View
          </Typography>
        </Stack>

        <PickemControlBar
          leaderboardData={leaderboardData as PickemLeaderboardData}
        />

        <Card>
          {pickemIsLoading ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center' }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <PickemLeaderboardTable />
          )}
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 200,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        {Boolean(pickemId) && (
          <MenuItem
            onClick={() =>
              navigate(
                `/dashboard/user-selections?pickem_id=${pickemId}&user_id=${openId}`
              )
            }
          >
            <Iconify icon={'eva:eye-outline'} sx={{ mr: 1 }} />
            User Selections
          </MenuItem>
        )}
      </Popover>
    </>
  );
};
